import { useCallback, useState } from "react";

import { type ReplyContext } from "../types";

/**
 * Hook to manage reply-to message state in chat.
 * Provides methods to set and clear the reply context.
 *
 * @example
 * ```tsx
 * const { replyTo, setReplyTo, clearReply } = useReplyState();
 *
 * // When user clicks "Reply" on a message
 * setReplyTo({
 *   messageId: 123,
 *   message: "Original message text",
 *   senderName: "John Doe"
 * });
 *
 * // When user cancels reply or sends message
 * clearReply();
 * ```
 */
export function useReplyState() {
  // eslint-disable-next-line react/hook-use-state
  const [replyTo, setReplyToState] = useState<ReplyContext | null>(null);

  const setReplyTo = useCallback((context: ReplyContext) => {
    setReplyToState(context);
  }, []);

  const clearReply = useCallback(() => {
    setReplyToState(null);
  }, []);

  return {
    replyTo,
    setReplyTo,
    clearReply,
  };
}
